import { NavLink, Outlet } from "react-router-dom";
import {
  Bell,
  Building2,
  CreditCard,
  Globe,
  Receipt,
  SlidersHorizontal,
  Users,
} from "lucide-react";
import { PageHeader } from "@/components/v2/PageHeader";
import { useTenantMe } from "@/lib/tenant-me";

/**
 * `/workspace/*` — tenant-scoped settings. Everything here lives on
 * the `tenants` table (or related per-tenant tables like `payment_methods`,
 * `customer_custom_fields`). Operators with multiple workspaces see
 * different values per workspace.
 *
 * The header explicitly names the current workspace so the operator
 * can tell at a glance "I'm editing settings for THIS tenant."
 */

const TABS = [
  { to: "/workspace/general", icon: Building2, label: "General" },
  { to: "/workspace/regional", icon: Globe, label: "Regional" },
  { to: "/workspace/notifications", icon: Bell, label: "Notifications" },
  { to: "/workspace/team", icon: Users, label: "Team" },
  { to: "/workspace/billing", icon: CreditCard, label: "Billing" },
  { to: "/workspace/payment-methods", icon: Receipt, label: "Payment methods" },
  { to: "/workspace/custom-fields", icon: SlidersHorizontal, label: "Custom fields" },
];

export function WorkspaceLayout() {
  const tenant = useTenantMe();
  const tenantName = tenant.data?.tenant?.name;

  return (
    <div className="px-6 py-6 space-y-6">
      <PageHeader
        title="Workspace"
        subtitle={
          tenantName
            ? `Settings for ${tenantName}. Apply to everyone in this workspace.`
            : "Settings that apply to everyone in this workspace."
        }
      />

      <div className="grid gap-6 md:grid-cols-[220px_1fr]">
        <nav className="space-y-0.5">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <NavLink
                key={t.to}
                to={t.to}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-foreground hover:bg-muted/60"
                  }`
                }
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span>{t.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <main className="min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
